import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import { FiChevronLeft, FiChevronRight, FiLogOut, FiSettings, FiUser } from "react-icons/fi";
import { FaCheckCircle, FaCircleNotch, FaTimesCircle } from "react-icons/fa";
import { FaMoon, FaSun } from "react-icons/fa";
import "./Header.css";
import { typeIcons } from "./Utils";
import { useUserPermissions } from "../hooks/useUserPermissions";

const Header = ({
  user,
  onLogout,
  onToggleRightPanel = () => {},
  isRightPanelOpen,
  rightPanelTaskIndicator,
  titleRef, // callback ref for the page title element (see PageTitle.jsx)
}) => {
  const { permissions, loading: permissionsLoading } = useUserPermissions();
  const isAdministrator =
    !permissionsLoading && Boolean(permissions?.is_administrator);

  const [theme, setTheme] = useState(() => {
    if (user?.settings?.theme) return user.settings.theme;
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const searchRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // Apply theme to <html>
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  // Persist default theme if user has no theme set
  useEffect(() => {
    if (user && (!user.settings || !user.settings.theme)) {
      (async () => {
        try {
          await axios.patch(`/users/${user.id}/`, { settings: { theme } });
        } catch (err) {
          console.error("Failed to persist default theme", err);
        }
      })();
    }
  }, [user, theme]);

  const toggleTheme = async () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);

    // Persist to backend
    try {
      await axios.patch(`/users/${user.id}/`, {
        settings: { theme: newTheme },
      });
    } catch (err) {
      console.error("Failed to update theme in user settings", err);
    }
  };

  // Search handling
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const delayDebounce = setTimeout(() => {
      setLoading(true);
      axios
        .get(`/search`, { params: { q: query } })
        .then((res) => setResults(res.data.results || []))
        .catch((err) => console.error("Search error", err))
        .finally(() => setLoading(false));
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [query]);

  // Close search and menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setQuery("");
        setResults([]);
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="header">
      {/* The current page's title, rendered here by its <PageTitle> */}
      <div className="header-title" ref={titleRef} />

      {/* Search bar (administrators only), on the right beside the user's buttons */}
      {isAdministrator && (
        <div className="header-search" ref={searchRef}>
          <input autoComplete="off"
            type="text"
            placeholder="Search contacts/accounts..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {loading && (
            <ul className="search-results">
              <li>Searching...</li>
            </ul>
          )}
          {results.length > 0 && (
            <ul className="search-results">
              {results.map((r) => (
                <li key={`${r.type}-${r.id}`}>
                  <Link
                    to={`/${r.type}?id=${r.id}`}
                    className="search-result-link"
                  >
                    {typeIcons[r.type] || <span>[MISSING ICON]</span>}
                    <strong> {r.label}</strong>
                    {r.type === "contact" ? (
                      <>
                        {r.details && <span> – {r.details}</span>}
                        {r.extra && <span>, {r.extra}</span>}
                      </>
                    ) : (
                      r.details && <span> – {r.details}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Right side */}
      <div className="user-info">
        {/* The signed-in user: a link to their own contact page (contacts are
            admin-only, so for everyone else just the icon, with their name on hover). */}
        {(() => {
          const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email || "";
          const canOpenContact = Boolean(user?.user_contact_id) && Boolean(permissions?.can_view_contacts);
          // A button like Settings and the panel toggle beside it (same look and
          // hover); Ctrl/Cmd-click or middle-click still opens it in a new tab.
          const contactUrl = `/contact?id=${user?.user_contact_id}`;
          return canOpenContact ? (
            <button
              type="button"
              className="icon-button"
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey) window.open(contactUrl, "_blank");
                else navigate(contactUrl);
              }}
              onAuxClick={(e) => {
                if (e.button === 1) window.open(contactUrl, "_blank");
              }}
              title={fullName ? `${fullName} (your contact page)` : "Your contact page"}
              aria-label={fullName ? `${fullName}: open your contact page` : "Open your contact page"}
            >
              <FiUser className="nav-icon" />
            </button>
          ) : (
            <span
              className="icon-button header-user-button is-static"
              title={fullName}
              aria-label={fullName ? `Signed in as ${fullName}` : "Signed in"}
              role="img"
            >
              <FiUser className="nav-icon" />
            </span>
          );
        })()}

        {/* Settings dropdown */}
        <div className="settings-menu" ref={menuRef}>
          <button
            className="icon-button"
            onClick={() => setMenuOpen((prev) => !prev)}
            title="Settings"
          >
            <FiSettings className="nav-icon" />
          </button>

          {menuOpen && (
            <ul className="dropdown-menu">
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    navigate("/settings");
                    setMenuOpen(false);
                  }}
                >
                  Go to Settings
                </button>
              </li>
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    toggleTheme();
                    setMenuOpen(false);
                  }}
                >
                  {theme === "dark" ? (
                    <>
                      <FaSun className="nav-icon" /> Light Mode
                    </>
                  ) : (
                    <>
                      <FaMoon className="nav-icon" /> Dark Mode
                    </>
                  )}
                </button>
              </li>
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    onLogout();
                    setMenuOpen(false);
                  }}
                >
                  <FiLogOut className="nav-icon" /> Log Out
                </button>
              </li>
            </ul>
          )}
        </div>

        <button
          type="button"
          className="icon-button header-panel-toggle"
          onClick={onToggleRightPanel}
          aria-pressed={Boolean(isRightPanelOpen)}
          title={isRightPanelOpen ? "Close right panel" : "Open right panel"}
        >
          {isRightPanelOpen ? (
            <FiChevronRight className="nav-icon" />
          ) : (
            <FiChevronLeft className="nav-icon" />
          )}

          {rightPanelTaskIndicator && (
            <span
              className={`header-panel-indicator header-panel-indicator-${rightPanelTaskIndicator}`}
              aria-hidden="true"
            >
              {rightPanelTaskIndicator === "pending" ? (
                <FaCircleNotch className="header-panel-indicator-icon header-panel-indicator-spin" />
              ) : rightPanelTaskIndicator === "success" ? (
                <FaCheckCircle className="header-panel-indicator-icon" />
              ) : (
                <FaTimesCircle className="header-panel-indicator-icon" />
              )}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default Header;
